import { useState } from "react";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { LoginModal } from "@/components/auth/login-modal";
import { useLPAccount, useLPPoolStats, useLPEarnings, useLPEarningsChart } from "./hooks/use-lp-data";
import { LPWithdrawalModal } from "./components/lp-withdrawal-modal";
import { LPEarningsChart } from "./components/lp-earnings-chart";
import {
  DateRangeSelector,
  type DateRangeValue,
  toApiPeriod,
} from "@/components/ui/date-range-selector";
import { Wallet, UserIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/utils";

export const LPDashboardPage = () => {
  const { isAuthenticated, userId } = useUnifiedAuth();
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [dateRange, setDateRange] = useState<DateRangeValue>("all");

  const { data: lpAccount, isLoading: accountLoading } = useLPAccount(userId || "");
  const { data: poolStats, isLoading: statsLoading } = useLPPoolStats(userId || "");
  
  // Convert date range to API period format
  const apiPeriod = toApiPeriod(dateRange);
  const { data: earnings } = useLPEarnings(userId || "", apiPeriod);
  const { data: chartData = [], isLoading: chartLoading } = useLPEarningsChart(apiPeriod);

  if (!isAuthenticated) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <UserIcon className="size-8 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Log In to Continue</h2>
          <p className="text-muted-foreground mb-6">
            Log in to view your LP dashboard and earnings.
          </p>
          <Button onClick={() => setLoginModalOpen(true)} size="lg">
            Log In
          </Button>
          <LoginModal open={loginModalOpen} onOpenChange={setLoginModalOpen} />
        </div>
      </div>
    );
  }

  if (accountLoading || statsLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!lpAccount?.account) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Wallet className="size-8 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Not an LP Provider</h2>
          <p className="text-muted-foreground mb-6">
            You don't have an LP account yet. Contact the platform team to become a Liquidity Provider.
          </p>
        </div>
      </div>
    );
  }

  const account = lpAccount.account;
  const depositedAmount = parseFloat(account.deposited_amount || "0");
  const withdrawnAmount = parseFloat(account.withdrawn_amount || "0");
  const totalEarned = parseFloat(account.total_earned || "0");
  const poolSharePercent = (account.pool_share_bps / 100).toFixed(2);
  const currentValue = depositedAmount + totalEarned - withdrawnAmount;

  // Calculate earnings breakdown
  const traderPnlEarnings = (earnings as any)?.earnings
    .filter((e: any) => e.event_type === "counterparty_pnl")
    .reduce((sum: number, e: any) => sum + parseFloat(e.lp_amount), 0) || 0;
  
  const tradingFeeEarnings = (earnings as any)?.earnings
    .filter((e: any) => e.event_type === "trading_fee")
    .reduce((sum: number, e: any) => sum + parseFloat(e.lp_amount), 0) || 0;
  
  const liquidationEarnings = (earnings as any)?.earnings
    .filter((e: any) => e.event_type === "liquidation")
    .reduce((sum: number, e: any) => sum + parseFloat(e.lp_amount), 0) || 0;
  
  const fundingEarnings = (earnings as any)?.earnings
    .filter((e: any) => e.event_type === "funding")
    .reduce((sum: number, e: any) => sum + parseFloat(e.lp_amount), 0) || 0;

  return (
    <main className="flex-1 p-4 md:p-6 overflow-auto bg-background">
      <div className="max-w-[1600px] mx-auto space-y-4 md:space-y-6">
        {/* Header Card */}
        <div className="rounded-lg border border-border/50 bg-card shadow-sm dark:shadow-none p-3 md:px-5 md:py-4">
          {/* Mobile Layout */}
          <div className="lg:hidden space-y-3">
            <div>
              <h1 className="text-xl font-bold text-foreground">LP Dashboard</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Liquidity provision and earnings
              </p>
            </div>
            <DateRangeSelector value={dateRange} onChange={setDateRange} />
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-foreground">LP Dashboard</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Liquidity provision and earnings
              </p>
            </div>
            <DateRangeSelector value={dateRange} onChange={setDateRange} />
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {/* My Deposited Amount */}
          <div className="rounded-2xl border border-border/60 bg-card/50 p-4 md:p-6 shadow-sm backdrop-blur-sm">
            <div className="text-sm text-muted-foreground mb-2">
              <span>My Deposit</span>
            </div>
            <div className="text-2xl md:text-3xl font-bold">${formatCurrency(depositedAmount, 2)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Withdrawn: ${formatCurrency(withdrawnAmount, 2)}
            </div>
          </div>

          {/* Pool Share */}
          <div className="rounded-2xl border border-border/60 bg-card/50 p-4 md:p-6 shadow-sm backdrop-blur-sm">
            <div className="text-sm text-muted-foreground mb-2">
              <span>My Pool Share</span>
            </div>
            <div className="text-2xl md:text-3xl font-bold">{poolSharePercent}%</div>
            <div className="text-xs text-muted-foreground mt-1">
              Of total pool
            </div>
          </div>

          {/* Current Value */}
          <div className="rounded-2xl border border-border/60 bg-card/50 p-4 md:p-6 shadow-sm backdrop-blur-sm">
            <div className="text-sm text-muted-foreground mb-2">
              <span>Current Value</span>
            </div>
            <div className="text-2xl md:text-3xl font-bold text-emerald-500">${formatCurrency(currentValue, 2)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Deposit + Earnings
            </div>
          </div>

          {/* Total Earned */}
          <div className="rounded-2xl border border-border/60 bg-card/50 p-4 md:p-6 shadow-sm backdrop-blur-sm">
            <div className="text-sm text-muted-foreground mb-2">
              <span>Total Earned</span>
            </div>
            <div className="text-2xl md:text-3xl font-bold text-emerald-500">${formatCurrency(totalEarned, 2)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              All-time earnings
            </div>
          </div>
        </div>

        {/* Chart + Combined Earnings - 2 Column Layout (50/50) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* LP Pool Earnings Chart - Left Column */}
          <div className="rounded-2xl border border-border/60 bg-card/50 shadow-sm backdrop-blur-sm">
            <LPEarningsChart 
              data={chartData} 
              isLoading={chartLoading}
              dateRange={dateRange}
            />
          </div>

          {/* Combined Earnings & Pool Stats - Right Column */}
          <div className="space-y-4">
            {/* Merged Earnings Breakdown + Pool Statistics */}
            <div className="rounded-2xl border border-border/60 bg-card/50 p-4 md:p-6 shadow-sm backdrop-blur-sm">
              <h3 className="text-base font-semibold mb-2">Earnings Breakdown ({apiPeriod})</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
                {/* Trader P&L Mini-Card */}
                <div className="rounded-xl border border-border bg-secondary/50 p-3 md:p-4 hover:bg-accent/50 transition-colors">
                  <div className="text-xs text-muted-foreground mb-1">Trader P&L</div>
                  <div className={`text-lg md:text-xl font-bold ${traderPnlEarnings >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                    ${formatCurrency(traderPnlEarnings, 2)}
                  </div>
                </div>

                {/* Trading Fees Mini-Card */}
                <div className="rounded-xl border border-border bg-secondary/50 p-3 md:p-4 hover:bg-accent/50 transition-colors">
                  <div className="text-xs text-muted-foreground mb-1">Trading Fees</div>
                  <div className="text-lg md:text-xl font-bold text-emerald-500">
                    ${formatCurrency(tradingFeeEarnings, 2)}
                  </div>
                </div>

                {/* Liquidation Surplus Mini-Card */}
                <div className="rounded-xl border border-border bg-secondary/50 p-3 md:p-4 hover:bg-accent/50 transition-colors">
                  <div className="text-xs text-muted-foreground mb-1">Liquidations</div>
                  <div className="text-lg md:text-xl font-bold text-emerald-500">
                    ${formatCurrency(liquidationEarnings, 2)}
                  </div>
                </div>

                {/* Funding Payments Mini-Card */}
                <div className="rounded-xl border border-border bg-secondary/50 p-3 md:p-4 hover:bg-accent/50 transition-colors">
                  <div className="text-xs text-muted-foreground mb-1">Funding</div>
                  <div className="text-lg md:text-xl font-bold text-emerald-500">
                    ${formatCurrency(fundingEarnings, 2)}
                  </div>
                </div>
              </div>

              {/* Pool Statistics */}
              {poolStats && (
                <>
                  <h3 className="text-base font-semibold mb-2 mt-4">Pool Statistics</h3>
                  <div className="grid grid-cols-3 gap-3 md:gap-4">
                  {/* 24h Earnings Mini-Card */}
                  <div className="rounded-xl border border-border bg-secondary/50 p-3 md:p-4 hover:bg-accent/50 transition-colors">
                    <div className="text-xs text-muted-foreground mb-1">24h Earnings</div>
                    <div className="text-lg md:text-xl font-bold text-emerald-500">
                      ${formatCurrency(parseFloat(poolStats.total_earnings || "0"), 2)}
                    </div>
                  </div>

                    {/* Total LPs Mini-Card */}
                    <div className="rounded-xl border border-border bg-secondary/50 p-3 md:p-4 hover:bg-accent/50 transition-colors">
                      <div className="text-xs text-muted-foreground mb-1">Total LPs</div>
                      <div className="text-lg md:text-xl font-bold">
                        {poolStats.total_lps || 0}
                      </div>
                    </div>

                    {/* Pool Size Mini-Card */}
                    <div className="rounded-xl border border-border bg-secondary/50 p-3 md:p-4 hover:bg-accent/50 transition-colors">
                      <div className="text-xs text-muted-foreground mb-1">Pool Size</div>
                      <div className="text-lg md:text-xl font-bold">
                        ${formatCurrency(parseFloat(poolStats.pool_size || "0"), 2)}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Withdraw Funds Section */}
            <div className="rounded-2xl border border-border/60 bg-card/50 p-4 md:p-6 shadow-sm backdrop-blur-sm">
              <h3 className="text-lg font-semibold mb-2">Withdraw Funds</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Request a withdrawal from your LP account. Earnings can be withdrawn instantly, principal requires approval.
              </p>
              <Button 
                variant="outline" 
                onClick={() => setShowWithdrawalModal(true)}
                className="w-full h-11"
              >
                Request Withdrawal
              </Button>
            </div>
          </div>
        </div>

        {/* Recent Earnings - Full Width */}
        <div className="rounded-2xl border border-border/60 bg-card/50 shadow-sm backdrop-blur-sm overflow-hidden">
          <div className="px-4 md:px-6 py-4 border-b border-border/60">
            <h3 className="text-lg font-semibold">Recent Earnings</h3>
            <p className="text-sm text-muted-foreground">Last 30 days</p>
          </div>
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-muted-foreground sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold bg-secondary">Date</th>
                  <th className="px-4 py-3 text-left font-semibold bg-secondary">Source</th>
                  <th className="px-4 py-3 text-left font-semibold bg-secondary">Market</th>
                  <th className="px-4 py-3 text-right font-semibold bg-secondary">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(earnings as any)?.earnings.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">
                      No earnings yet
                    </td>
                  </tr>
                ) : (
                (earnings as any)?.earnings.slice(0, 20).map((earning: any) => (
                  <tr key={earning.id} className="border-t border-border/10 hover:bg-[rgb(var(--secondary))]/20">
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {new Date(earning.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 capitalize text-xs">{earning.event_type.replace("_", " ")}</td>
                    <td className="px-4 py-3 font-mono text-xs">{earning.market}</td>
                    <td className="px-4 py-3 text-right font-medium text-emerald-500 text-sm">
                      +${formatCurrency(parseFloat(earning.lp_amount), 2)}
                    </td>
                  </tr>
                ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Withdrawal Modal */}
      <LPWithdrawalModal
        isOpen={showWithdrawalModal}
        onClose={() => setShowWithdrawalModal(false)}
        userId={userId || ""}
      />
    </main>
  );
};

